import {
  Button,
  Group,
  Stack,
  Text,
  Title,
  Paper,
  SimpleGrid,
  Badge,
  Divider
} from '@mantine/core';
import Breadcrumbs, { BreadcrumbItem } from 'components/BreadcrumbsComponent';
import Card from 'components/Card';
import DataTables, { DataTableColumn } from 'components/DataTables';
import React from 'react';
import { HiOutlinePlus } from 'react-icons/hi';
import { pathnames } from 'routes/lcms';
import { useCourseCombineStore } from 'store/courseCombine';
import { useNavigate, useParams } from 'react-router-dom';
import { useCourseStore } from 'store/createCourseStore';
import { useQuery } from '@tanstack/react-query';
import {
  trainingCatalogueDetailService,
  trainingCatalogueCompetencyDetailService
} from 'services/lcms/trainingCatalogue.service';
import { trainingCatalogueParticipantsService } from 'services/lcms/trainingCatalogue.service';

const DetailCourseCombine = () => {
  const Field = ({ label, value }: { label: string; value: any }) => (
    <Stack gap={2}>
      <Text c='secondary.5' fz={12} tt='uppercase' fw={600}>
        {label}
      </Text>
      <Text fz={14} fw={500} c='dark'>
        {value || '-'}
      </Text>
    </Stack>
  );
  const navigate = useNavigate();
  const { reset, setFormMode } = useCourseStore();
  const { formData } = useCourseCombineStore();
  const { id } = useParams();

  const { data: detail, isPending } = useQuery({
    queryKey: ['trainingCatalogueDetail', id],
    queryFn: () => trainingCatalogueDetailService.detail(id || formData.trainingRecommendationID),
    enabled: !!(id || formData.trainingRecommendationID)
  });

  const { data: competencyDetail, isPending: isCompetencyPending } = useQuery({
    queryKey: ['trainingCatalogueCompetencyDetail', id],
    queryFn: () =>
      trainingCatalogueCompetencyDetailService.detail(id || formData.trainingRecommendationID),
    enabled: !!(id || formData.trainingRecommendationID)
  });

  const { data: participantsRes, isPending: isParticipantsPending } = useQuery({
    queryKey: ['trainingCatalogueParticipants', id],
    queryFn: () =>
      trainingCatalogueParticipantsService.list(id || formData.trainingRecommendationID),
    enabled: !!(id || formData.trainingRecommendationID)
  });

  const participants = participantsRes?.data || [];

  const participantColumns: DataTableColumn[] = [
    { heading: 'Name', props: 'participantName', id: 'participantName', active: true },
    { heading: 'Position', props: 'participantPosition', id: 'participantPosition', active: true },
    { heading: 'Binusian ID', props: 'binusianId', id: 'binusianId', active: true },
    { heading: 'Type', props: 'participantType', id: 'participantType', active: true },
    { heading: 'Level', props: 'participantLevel', id: 'participantLevel', active: true }
  ];

  const item = detail?.data;
  const competencyItem = competencyDetail?.data;
  const LOCAL_KEY = 'courseCombineCompareIds';

  const addAndGoToCompare = () => {
    if (!item?.trainingRecommendationID && !formData?.trainingRecommendationID) return;
    const id = item?.trainingRecommendationID || formData.trainingRecommendationID;
    try {
      const raw = localStorage.getItem(LOCAL_KEY);
      const arr: string[] = raw ? JSON.parse(raw) : [];
      if (!arr.includes(id)) {
        if (arr.length >= 12) return; // max 12
        arr.push(id);
        localStorage.setItem(LOCAL_KEY, JSON.stringify(arr));
      }
      // navigate after ensuring it's stored
      navigate(pathnames.courseCombineTNACompare);
    } catch {
      /* ignore */
    }
  };

  const breadcrumbs = [
    { title: 'Course TNA', href: pathnames.courseCombineTNA },
    { title: 'Detail Course TNA', href: '' }
  ];
  return (
    <>
      <Breadcrumbs items={breadcrumbs as BreadcrumbItem[]} mb={32} />

      <Title order={2} mb={30}>
        {item?.courseName || formData?.courseName}
      </Title>
      <Group mb={20} gap='xs' justify='flex-end' wrap='wrap'>
        <Button
          variant='default'
          leftSection={<HiOutlinePlus />}
          onClick={() => {
            reset();
            setFormMode('add');
            navigate(pathnames.createCourses);
          }}
        >
          Create Course
        </Button>
        <Button variant='filled' onClick={addAndGoToCompare}>
          Add & Compare
        </Button>
      </Group>
      <Card>
        <Stack gap={24}>
          <Stack gap={4}>
            <Group gap={8} wrap='wrap'>
              {item?.moduleCode && (
                <Badge variant='light' color='blue'>
                  {item.moduleCode}
                </Badge>
              )}
              {item?.trainingCategoryDesc && (
                <Badge variant='outline' color='teal'>
                  {item.trainingCategoryDesc}
                </Badge>
              )}
              {item?.trainingModelDesc && (
                <Badge variant='outline' color='purple'>
                  {item.trainingModelDesc}
                </Badge>
              )}
            </Group>
          </Stack>
          <Divider />
          {(isPending || isCompetencyPending || isParticipantsPending) && (
            <Text fz={14}>Loading...</Text>
          )}
          {!isPending && !isCompetencyPending && !isParticipantsPending && item && (
            <>
              <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing={24} verticalSpacing={28}>
                <Field label='Module Code' value={item.moduleCode} />
                <Field label='Program Name' value={item.programName} />
                <Field label='PNA Year' value={item.pnaYear} />
                <Field label='Training Category' value={item.trainingCategoryDesc} />
                <Field label='Training Method' value={item.trainingMethodName} />
                <Field label='Learning Hours' value={item.learningHours} />
                <Field
                  label='Budget'
                  value={item.budget ? `Rp ${item.budget.toLocaleString()}` : '-'}
                />
                <Field
                  label='Total Cost'
                  value={item.totalCost ? `Rp ${item.totalCost.toLocaleString()}` : '-'}
                />
                <Field label='Training Provider' value={item.trainingProvider} />
                <Field label='Schedule' value={`${item.schedule} (${item.scheduleDesc})`} />
                <Field label='Competency' value={item.competencyName} />
                <Field label='Competency Category' value={competencyItem?.competencyCategoryDesc} />
                <Field label='Urgency' value={competencyItem?.urgencyDesc} />
                <Field label='Competency Development' value={item.competencyDevelopDesc} />
                <Field label='Level Impact' value={item.levelImpactDesc} />
                <Field label='Training Model' value={item.trainingModelDesc} />
                <Field label='Strategic Objectives' value={item.strategicObjectives} />
                <Field label='Performance Measures' value={item.performanceMeasures} />
                <Field label='Activity Code' value={item.activityCode} />
              </SimpleGrid>
              <Divider mt={8} />

              {/* Participants section */}
              <Divider />
              <Stack gap={8}>
                <Text c='secondary.5' fz={12} tt='uppercase' fw={600}>
                  Participants
                </Text>
                <Paper withBorder p={18} radius='md'>
                  <DataTables
                    key={`participants-${item?.trainingRecommendationID || formData?.trainingRecommendationID}`}
                    columns={participantColumns}
                    data={participants}
                    loading={isParticipantsPending}
                    withPagination={false}
                    isNumbering
                    totalData={participants.length}
                    emptyMessage='-'
                  />
                </Paper>
              </Stack>
            </>
          )}
        </Stack>
      </Card>
    </>
  );
};

export default DetailCourseCombine;
